/** Shared by the signup bundle, standalone bootstrap, and student app. */
export const OnboardingSteps = [
    'about_you', 'starting_point', 'target_score', 'accommodations',
    'time_and_test_date', 'plan_style', 'live_classes',
] as const;
export type OnboardingStep = typeof OnboardingSteps[number];
export type AssessmentMethod = 'quick' | 'sections' | 'full';
export type SignupMethod = 'email' | 'google';
export type FunnelError = 'validation' | 'authentication' | 'captcha' | 'profile_save' | 'plan_save' | 'assessment_save' | 'unknown';

export type FunnelContext = {
    enabled: boolean;
    environment: string;
    debug: boolean;
    userId: string | null;
    signupCreated: boolean;
};
type Journey = {
    version: 1;
    createdAt: number;
    cohort: 'new_signup' | 'existing_account';
    method: SignupMethod | null;
    startedAt: number | null;
    completedAt: number | null;
    assessment: AssessmentMethod | null;
    seen: string[];
    stepViewedAt: Partial<Record<OnboardingStep, number>>;
};
type Runtime = { journeys: Record<string, Journey>; initialized: Set<string> };
declare global {
    interface Window {
        lsatlabFunnelContext?: FunnelContext;
        lsatlabFunnelRuntime?: Runtime;
    }
}
// v2: LSA-28 dropped the tests_and_sections step; Create completes live_classes.
const Version = 'plan_first_v2';
const Prefix = 'lsatlab:funnel:v1:';
const Lifetime = 30 * 24 * 60 * 60 * 1000;
const isEligible = (): boolean => typeof window !== 'undefined'
    && window.lsatlabFunnelContext?.enabled === true
    && !new URLSearchParams(window.location.search).has('userid');
const key = () => Prefix + (window.lsatlabFunnelContext?.userId ?? 'anonymous');
const runtime = (): Runtime => window.lsatlabFunnelRuntime ??= { journeys: {}, initialized: new Set() };

function read(storedKey: string): Journey | null {
    try {
        const value: unknown = JSON.parse(localStorage.getItem(storedKey) ?? 'null');
        if (!value || typeof value !== 'object') return null;
        const j = value as Journey;
        if (j.version !== 1 || !Number.isFinite(j.createdAt) || Date.now() - j.createdAt > Lifetime
            || j.createdAt > Date.now() || !Array.isArray(j.seen) || !j.seen.every(x => typeof x === 'string')
            || !j.stepViewedAt || typeof j.stepViewedAt !== 'object'
            || !Object.values(j.stepViewedAt).every(x => Number.isFinite(x))
            || !['new_signup', 'existing_account'].includes(j.cohort)
            || (j.method !== null && !['email', 'google'].includes(j.method))
            || (j.assessment !== null && !['quick', 'sections', 'full'].includes(j.assessment))
            || (j.startedAt !== null && !Number.isFinite(j.startedAt))
            || (j.completedAt !== null && !Number.isFinite(j.completedAt))) return null;
        return j;
    } catch { return null; }
}
function persist(journey: Journey): void {
    try { localStorage.setItem(key(), JSON.stringify(journey)); } catch { /* Storage is optional. */ }
}
function journey(): Journey {
    const state = runtime();
    const storedKey = key();
    if (state.journeys[storedKey]) return state.journeys[storedKey];
    let j = read(storedKey);
    if (!j && window.lsatlabFunnelContext?.signupCreated) {
        j = state.journeys[Prefix + 'anonymous'] ?? read(Prefix + 'anonymous');
        try { localStorage.removeItem(Prefix + 'anonymous'); } catch { /* Optional storage. */ }
        delete state.journeys[Prefix + 'anonymous'];
    }
    j ??= { version: 1, createdAt: Date.now(), cohort: 'existing_account', method: null,
        startedAt: null, completedAt: null, assessment: null, seen: [], stepViewedAt: {} };
    if (window.lsatlabFunnelContext?.signupCreated || !window.lsatlabFunnelContext?.userId) j.cohort = 'new_signup';
    state.journeys[storedKey] = j;
    persist(j);
    return j;
}

type Metadata = {
    step_name?: OnboardingStep;
    elapsed_ms?: number;
    error_code?: FunnelError;
    assessment_type?: AssessmentMethod;
};
function emit(event: string, metadata: Metadata = {}, once?: string): boolean {
    if (!isEligible()) return false;
    const j = journey();
    if (once && j.seen.includes(once)) return false;
    const context = window.lsatlabFunnelContext!;
    try {
        const target = window as unknown as { dataLayer?: unknown[] };
        target.dataLayer ??= [];
        // Clear unused keys on every event: GTM's data model otherwise retains
        // the previous step's error/type/elapsed time on unrelated events.
        target.dataLayer.push({
            event, funnel_version: Version, flow_type: j.cohort,
            analytics_environment: context.environment, debug_mode: context.debug,
            user_id: context.userId, method: j.method,
            step_name: metadata.step_name ?? null,
            step_number: metadata.step_name ? OnboardingSteps.indexOf(metadata.step_name) + 1 : null,
            elapsed_ms: metadata.elapsed_ms ?? null,
            error_code: metadata.error_code ?? null,
            assessment_type: metadata.assessment_type ?? null,
        });
        if (once) j.seen.push(once);
        persist(j);
        return true;
    } catch { return false; }
}

export function initializeFunnel(): void {
    if (!isEligible() || runtime().initialized.has(key())) return;
    runtime().initialized.add(key());
    const j = journey();
    if (window.lsatlabFunnelContext?.signupCreated) emit('sign_up', {}, 'sign_up');
    if (/^\/sign-up\/?$/.test(window.location.pathname)) emit('signup_viewed', {}, 'signup_viewed');
    // Flush successful plan creation on the destination.
    // Keeping it pending until this page avoids losing conversion
    // to a fast unload. GA4 receives exactly one event from this bootstrap.
    if (j.completedAt !== null && !j.seen.includes('onboarding_completed')) {
        emit('onboarding_completed', { elapsed_ms: j.completedAt - (j.startedAt ?? j.createdAt) }, 'onboarding_completed');
    }
}
export function signupAttempt(method: SignupMethod): void {
    if (!isEligible()) return;
    initializeFunnel();
    journey().method = method;
    emit('signup_attempted');
}
export function signupError(error: FunnelError): void { initializeFunnel(); emit('signup_error', { error_code: error }); }
export function viewOnboardingStep(step: OnboardingStep): void {
    if (!isEligible() || !window.lsatlabFunnelContext?.userId) return;
    initializeFunnel();
    const j = journey();
    if (j.completedAt !== null) return;
    j.startedAt ??= Date.now();
    emit('onboarding_started', {}, 'onboarding_started');
    j.stepViewedAt[step] ??= Date.now();
    emit('onboarding_step_viewed', { step_name: step }, 'view:' + step);
}
export function completeOnboardingStep(step: OnboardingStep): void {
    if (!isEligible()) return;
    const j = journey();
    if (!j.startedAt || j.completedAt !== null) return;
    emit('onboarding_step_completed', { step_name: step,
        elapsed_ms: Math.max(0, Date.now() - (j.stepViewedAt[step] ?? Date.now())) }, 'complete:' + step);
}
export function onboardingError(step: OnboardingStep, error: FunnelError): void {
    if (!isEligible() || !journey().startedAt || journey().completedAt !== null) return;
    emit('onboarding_step_error', { step_name: step, error_code: error });
}
export function assessmentDeparted(type: AssessmentMethod): void {
    if (!isEligible() || !journey().startedAt || journey().completedAt !== null) return;
    journey().assessment = type;
    emit('onboarding_assessment_started', { step_name: 'starting_point', assessment_type: type });
}
export function assessmentReturned(completed: boolean): void {
    if (!isEligible()) return;
    const j = journey();
    if (!j.assessment || j.completedAt !== null) return;
    emit(completed ? 'onboarding_assessment_completed' : 'onboarding_assessment_returned',
        { step_name: 'starting_point', assessment_type: j.assessment },
        completed ? 'assessment-completed:' + j.assessment : 'assessment-returned:' + j.assessment);
    if (completed) { j.assessment = null; persist(j); }
}
export function onboardingCompleted(): void {
    if (!isEligible()) return;
    const j = journey();
    if (!j.startedAt || j.completedAt !== null) return;
    // Live Classes is the last question: Create completes it (LSA-28 dropped
    // the Tests & Sections step that used to follow).
    completeOnboardingStep('live_classes');
    j.completedAt = Date.now();
    persist(j);
    // The next page's standalone bootstrap emits completion after navigation.
    // With blocked storage, emit immediately because a reload cannot recover it.
    if (!read(key())) emit('onboarding_completed', { elapsed_ms: j.completedAt - j.startedAt }, 'onboarding_completed');
}
export function studyActivityStarted(activityId: number): void {
    if (!isEligible()) return;
    initializeFunnel();
    const j = journey();
    if (!window.lsatlabFunnelContext?.userId || !Number.isFinite(activityId)) return;
    emit('study_activity_started', { elapsed_ms: j.completedAt === null ? undefined : Math.max(0, Date.now() - j.completedAt) }, 'activity:' + activityId);
}

/** Give queued GTM tags a bounded chance to run before an assessment redirect. */
export function waitForFunnelTags(): Promise<void> {
    if (!isEligible()) return Promise.resolve();
    return new Promise(resolve => {
        const timer = window.setTimeout(resolve, 250);
        const done = () => { window.clearTimeout(timer); resolve(); };
        try {
            const target = window as unknown as { dataLayer?: unknown[] };
            target.dataLayer ??= [];
            target.dataLayer.push({ event: 'onboarding_navigation', eventCallback: done, eventTimeout: 250 });
        } catch { done(); }
    });
}
